import { useEffect, useState } from "react";
import { http } from "../api";
import { Compass, MapPin } from "lucide-react";
import { Modal, PageHeader, AddButton, FormRow, EmptyState } from "../shared";

const empty = { household_id: "", address: "", ward: 7, booth: "42", gps_lat: null, gps_lng: null, outcome: "Contacted", notes: "", follow_up: "" };
const outcomes = ["Contacted", "Not Home", "Refused", "Follow-up needed"];
function tone(o) { return { Contacted: "green", "Not Home": "amber", Refused: "red", "Follow-up needed": "blue" }[o] || "blue"; }

export default function FieldOps() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [gpsStatus, setGpsStatus] = useState("");

  const load = () => http.get("/field-visits").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const captureGps = () => {
    setGpsStatus("Capturing…");
    if (!navigator.geolocation) { setGpsStatus("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm((f) => ({ ...f, gps_lat: p.coords.latitude, gps_lng: p.coords.longitude })); setGpsStatus(`✓ ${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)}`); },
      () => setGpsStatus("Permission denied"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const save = async () => {
    setSaving(true);
    try { await http.post("/field-visits", { ...form, ward: Number(form.ward) }); setShow(false); setForm(empty); setGpsStatus(""); load(); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader eyebrow="DOOR-TO-DOOR" title="Field visits" subtitle={`${rows.length} household surveys logged`}
        actions={<AddButton onClick={() => setShow(true)} label="Log visit" testid="field-add-button" />} />

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={Compass} title="No field visits yet" hint="Log a household visit with GPS." /> : (
          <div className="visit-list">
            {rows.map((v, i) => (
              <div key={v.visit_id} className="visit-row" data-testid={`visit-row-${i}`}>
                <div>
                  <b>{v.address}</b>
                  <small><MapPin size={12} /> Booth {v.booth}{v.gps_lat && v.gps_lng ? ` · ${v.gps_lat.toFixed(4)}, ${v.gps_lng.toFixed(4)}` : ""} · by {v.visited_by}</small>
                  {v.notes && <p className="notes">{v.notes}</p>}
                  {v.follow_up && <small className="hint">Follow-up: {v.follow_up}</small>}
                </div>
                <span className={`chip ${tone(v.outcome)}`}>{v.outcome}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {show && (
        <Modal title="Log field visit" onClose={() => { setShow(false); setGpsStatus(""); }} testid="visit-modal">
          <FormRow label="Address"><input data-testid="form-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></FormRow>
          <div className="form-grid">
            <FormRow label="Booth"><input data-testid="form-booth-input" value={form.booth} onChange={(e) => setForm({ ...form, booth: e.target.value })} /></FormRow>
            <FormRow label="Outcome"><select data-testid="form-outcome-input" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })}>{outcomes.map((o) => <option key={o}>{o}</option>)}</select></FormRow>
            <FormRow label="Follow-up date"><input data-testid="form-followup-input" type="date" value={form.follow_up} onChange={(e) => setForm({ ...form, follow_up: e.target.value })} /></FormRow>
          </div>
          <FormRow label="Notes"><input data-testid="form-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormRow>
          <div className="gps-row">
            <button className="outline" data-testid="capture-gps-button" onClick={captureGps}><MapPin size={15} /> Capture live location</button>
            <small className="hint" data-testid="gps-status">{gpsStatus || "Not captured"}</small>
          </div>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShow(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.address} onClick={save}>{saving ? "Saving…" : "Save visit"}</button>
          </div>
        </Modal>
      )}
    </>
  );
}
